/**
 * Root layout component for all pages.
 *
 * Provides the app shell including:
 * - Header with navigation and auth status
 * - Main content area
 * - Footer with legal links
 * - Dialogs (Privacy, Terms, Account)
 */

import { observer } from 'mobx-react-lite';
import { Outlet, useLocation, useNavigate } from 'react-router';

import { Footer } from './Footer';

import { AccountDialog, ConsentDialog, GuestBanner } from '@/components/auth';
import { PrivacyDialog, TermsDialog } from '@/components/legal';
import { useStores } from '@/stores/StoreContext';

/**
 * Root layout component.
 */
export const RootLayout = observer(function RootLayout(): React.ReactElement {
  const { authStore } = useStores();
  const location = useLocation();
  const navigate = useNavigate();

  const isPrivacyOpen = location.pathname === '/privacy';
  const isTermsOpen = location.pathname === '/terms';
  // Show account dialog for any user with a server session (guest or authenticated)
  const isAccountOpen = location.pathname === '/account' && authStore.hasServerUser;
  // Hide footer on game pages (game has its own layout)
  const isGamePage = location.pathname.startsWith('/game');

  const handleDialogClose = (): void => {
    // Go back to previous page instead of always going to splash
    void navigate(-1);
  };

  return (
    <div className="flex min-h-screen flex-col">
      {/* Guest banner - shown when user is not logged in */}
      {authStore.status === 'guest' && <GuestBanner />}

      {/* Main content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer - hidden on game pages */}
      {!isGamePage && <Footer />}

      {/* Consent dialog - blocks until user consents */}
      <ConsentDialog
        open={authStore.needsConsent}
        onClose={() => {
          // Cannot close without consenting - do nothing
        }}
        onConsent={async (consent) => {
          await authStore.recordConsent({
            analyticsConsent: consent.analyticsConsent,
            marketingConsent: consent.marketingConsent,
          });
        }}
      />

      {/* Dialogs - route-based */}
      <PrivacyDialog open={isPrivacyOpen} onClose={handleDialogClose} />
      <TermsDialog open={isTermsOpen} onClose={handleDialogClose} />
      <AccountDialog open={isAccountOpen} onClose={handleDialogClose} />
    </div>
  );
});
